import { STREET_ASPHALT_BOTTOM_Y, STREET_ASPHALT_TOP_Y } from '../config.js';
import { CHARACTER_ART_H, centreYFromGround, perspectiveScale } from './carCatalog.mjs';

// The child at a zebra crossing. He waits on the pavement until the car has actually
// stopped for him (the rule engine's `pedestrian-go`), then walks across the road towards
// the camera (`npc_child_walk`), and once over carries on along the near-side verge to the
// right (`npc_child_walk_right`) until he is off the screen. If somebody drives through the
// crossing while he is on it he jumps back to the kerb — the mistake is already scored,
// this is just what it looks like.
//
//   const child = new PedestrianActor(scene, { x: zebra.x0 + 30, worldScale });
//   child.state;      // 'waiting' → 'crossing' → 'done' (done = over the road, walking away)
//   child.go();       // on `pedestrian-go`
//   child.jumpBack(); // on a crossing mistake
//   child.update(dt); // every frame (the walk along the verge)
//
// Without `npc_child` he falls back to a plain circle at the same real-world size — the
// whole crossing works with no art at all; the walking poses fall back to the standing one.

const FEET_WAIT_Y = 522;      // the kerb he waits on, just above the asphalt
const FEET_DONE_Y = 692;      // well into the near-side verge (the asphalt ends at world y 652)
const PAUSE_MS = 1000;        // a breath on the verge before he walks off
const CROSS_MS = 3500;        // how long he takes to walk across
const JUMP_MS = 300;          // …and to leap back out of the way
const WALK_PX_S = 130;        // …and how fast he strolls off along the verge (1.3 m/s)
const OFFSCREEN_PAD = 300;    // px past the route's right end before he is hidden
const FALLBACK_R = 40;        // the drawn stand-in's radius, at the reference plane

export class PedestrianActor {
  constructor(scene, { x = 0, worldScale = 1 } = {}) {
    this.scene = scene;
    this.state = 'waiting';
    this.x = x;
    this._h = CHARACTER_ART_H * worldScale;   // 1.2 m of child, at the route's real-world scale
    this._half = this._h / 2;
    this._feetY = FEET_WAIT_Y;
    this._tween = null;
    this._pauseTimer = null;
    this._walking = false;

    const key = scene.textures.exists('npc_child') ? 'npc_child' : null;
    if (key) {
      this._img = scene.add.image(x, 0, key);
      this._aspect = this._img.width / this._img.height || 0.5;
    } else {
      this._img = scene.add.circle(x, 0, FALLBACK_R, 0xffc94d).setStrokeStyle(4, 0x6b4b1a);
      this._aspect = null;
    }
    this._place();
  }

  // Everything about how he is drawn follows from where his FEET are: his size (he walks
  // towards the camera, so he grows — see carCatalog's PERSPECTIVE), his sprite centre and
  // his depth among the cars, which sort by their wheel line the same way.
  _place() {
    const s = perspectiveScale(this._feetY);
    if (this._aspect) this._img.setDisplaySize(this._h * s * this._aspect, this._h * s);
    else this._img.setScale(s);
    this._img.y = centreYFromGround(this._half, this._feetY);
    this._img.setDepth(this._feetY + 1);
  }

  // Swap to another pose of the same child (first key that exists wins); the stand-in
  // circle simply carries on being a circle.
  _pose(...keys) {
    if (!this._aspect) return;
    const key = keys.find((k) => this.scene.textures.exists(k));
    if (!key) return;
    this._img.setTexture(key);
    this._aspect = this._img.width / this._img.height || 0.5;
    this._place();
  }

  // Walk his feet to `toY`. The tween drives a plain `{ y }` proxy rather than the actor's
  // own field: Phaser skips properties whose name starts with an underscore, so tweening
  // `_feetY` directly finishes instantly and he never leaves the kerb.
  _walkFeetTo(toY, duration) {
    const feet = { y: this._feetY };
    this._tween?.remove();
    this._tween = this.scene.tweens.add({
      targets: feet, y: toY, duration, ease: duration === JUMP_MS ? 'Quad.easeOut' : 'Linear',
      onUpdate: () => { this._feetY = feet.y; this._place(); },
      onComplete: () => {
        this._tween = null;
        if (toY === FEET_DONE_Y) this._arrive();
      },
    });
  }

  // Over the road: stand for a second, then turn right and stroll off along the verge
  // (see update()).
  _arrive() {
    this.state = 'done';
    this._pose('npc_child', 'npc_child_walk');
    this._pauseTimer = this.scene.time.delayedCall(PAUSE_MS, () => {
      this._pauseTimer = null;
      if (!this._img) return;
      this._pose('npc_child_walk_right', 'npc_child_side', 'npc_child');
      this._walking = true;
    });
  }

  /** Step off the kerb: the car has stopped and it's his turn. */
  go() {
    if (this.state !== 'waiting') return;
    this.state = 'crossing';
    this._pose('npc_child_walk', 'npc_child_side');
    this._walkFeetTo(FEET_DONE_Y, CROSS_MS);
  }

  /** Somebody drove through the crossing: back to the kerb, fast. */
  jumpBack() {
    if (this.state === 'done' || !this._img) return;
    this._walkFeetTo(FEET_WAIT_Y, JUMP_MS);
  }

  /** One frame (`dt` in SECONDS, as RouteDirector hands it out): the walk along the verge
   *  once he is across — all the way to the right end of the route, then he is hidden. */
  update(dt) {
    if (!this._walking || !this._img || !this._img.visible) return;
    this.x += WALK_PX_S * dt;
    this._img.x = this.x;
    const end = (this.scene.worldWidth ?? Infinity) + OFFSCREEN_PAD;
    if (this.x > end) this._img.setVisible(false);
  }

  /** Is he standing on the asphalt (i.e. is there anything there to hit)? */
  onRoad() {
    return this._feetY >= STREET_ASPHALT_TOP_Y && this._feetY <= STREET_ASPHALT_BOTTOM_Y;
  }

  /** His footprint, in the same shape TrafficActor uses — or null when he's off the road. */
  bounds() {
    if (!this._img || !this.onRoad()) return null;
    const w = (this._aspect ? this._img.displayWidth : FALLBACK_R * 2) * 0.8;
    return { left: this.x - w / 2, right: this.x + w / 2, groundY: this._feetY };
  }

  destroy() {
    this._tween?.remove();
    this._tween = null;
    this._pauseTimer?.remove();
    this._pauseTimer = null;
    this._img?.destroy();
    this._img = null;
  }
}
